const MODE_FIXED = "fixed-annual"
const MODE_HOURLY = "hourly"

/**
 * Parses a CSV text string of rate card data and matches rows to existing lines by trade name.
 *
 * @param {string} csvText - Raw CSV text
 * @param {Array<{tradeId: string, tradeName: string, hourlyRate: number, minimumBillingPeriod: number|null, notes: string, mode?: string, fixedAnnualAmount?: number, contractedDaysPerYear?: number}>} existingLines
 * @returns {{ lines: Array, errors: Array<string>, warnings: Array<string> }}
 */
export function parseRateCardCsv(csvText, existingLines) {
    const errors = []
    const warnings = []

    const rows = parseCsvRows(csvText)
    if (rows.length < 2) {
        errors.push("CSV file is empty or has no data rows")
        return { lines: [], errors, warnings }
    }

    const headers = rows[0].map(normalizeHeader)
    const tradeNameCol = findColumn(headers, ["trade name"])
    const hourlyRateCol = findColumn(headers, ["hourly rate"])
    const minBillingCol = findColumn(headers, ["minimum billing period"])
    const notesCol = findColumn(headers, ["notes"])
    const rateTypeCol = findColumn(headers, ["rate type"])
    const fixedAnnualCol = findColumn(headers, ["fixed annual"])
    const contractedDaysCol = findColumn(headers, ["contracted days"])

    if (tradeNameCol === -1) {
        errors.push('Missing required column: "Trade Name"')
        return { lines: [], errors, warnings }
    }
    if (hourlyRateCol === -1) {
        errors.push('Missing required column: "Hourly Rate"')
        return { lines: [], errors, warnings }
    }

    const hasMinBilling = minBillingCol !== -1
    // Only round-trip the rate mode when the CSV actually carries a Rate Type column. Legacy
    // exports without it leave the existing line's mode/fixed amount untouched on import.
    const hasRateType = rateTypeCol !== -1
    const existingByName = buildTradeNameLookup(existingLines)
    const lines = []

    for (let i = 1; i < rows.length; i++) {
        const row = rows[i]
        const rowNum = i + 1
        const tradeName = (row[tradeNameCol] || "").trim()

        if (!tradeName) continue

        const match = existingByName[tradeName.toLowerCase()]
        if (!match) {
            errors.push(`Row ${rowNum}: Trade "${tradeName}" not found in skill map`)
            continue
        }

        const rawRate = (row[hourlyRateCol] || "").trim()
        const rawMinBilling = hasMinBilling ? (row[minBillingCol] || "").trim() : ""
        const rawNotes = notesCol !== -1 ? (row[notesCol] || "").trim() : ""
        // Match the exact "Fixed annual" label the export writes; startsWith avoids classifying
        // values like "not fixed" / "unfixed" as fixed-annual.
        const isFixed = hasRateType && (row[rateTypeCol] || "").trim().toLowerCase().startsWith("fixed")

        // A fixed-annual line bills a flat yearly amount; its hourly rate is unused, so a blank
        // hourly cell is expected and must not warn or fail the row.
        const hourlyRate = isFixed
            ? parseOptionalHourlyRate(rawRate, match.hourlyRate, rowNum, tradeName, errors)
            : parseHourlyRate(rawRate, rowNum, tradeName, errors, warnings)
        if (hourlyRate === null) continue

        const minimumBillingPeriod = parseMinBillingPeriod(
            rawMinBilling,
            hasMinBilling,
            rowNum,
            tradeName,
            errors,
            warnings
        )
        if (minimumBillingPeriod === false) continue

        const line = {
            tradeId: match.tradeId,
            tradeName: match.tradeName,
            hourlyRate,
            minimumBillingPeriod: minimumBillingPeriod === undefined ? null : minimumBillingPeriod,
            notes: rawNotes,
        }

        if (hasRateType) {
            line.mode = isFixed ? MODE_FIXED : MODE_HOURLY
            if (isFixed) {
                const rawFixed = fixedAnnualCol !== -1 ? (row[fixedAnnualCol] || "").trim() : ""
                const fixedAnnualAmount = parseFixedAnnual(rawFixed, rowNum, tradeName, errors)
                if (fixedAnnualAmount === null) continue
                line.fixedAnnualAmount = fixedAnnualAmount
            } else {
                // The export leaves dormant fixed terms blank on hourly rows because they do not
                // apply to the current rate. Preserve the stored value so an export/import cycle
                // cannot erase terms that should return when the trade switches back to fixed.
                line.fixedAnnualAmount = match.fixedAnnualAmount
            }
            // Only round-trip contracted capacity when the CSV carries the column, mirroring the
            // Rate Type handling — legacy exports leave the existing line's capacity untouched.
            if (contractedDaysCol !== -1) {
                if (isFixed) {
                    const rawDays = (row[contractedDaysCol] || "").trim()
                    const contractedDaysPerYear = parseContractedDays(rawDays, rowNum, tradeName, errors)
                    if (contractedDaysPerYear === false) continue
                    line.contractedDaysPerYear = contractedDaysPerYear
                } else {
                    line.contractedDaysPerYear = match.contractedDaysPerYear
                }
            }
        }

        lines.push(line)
    }

    return { lines, errors, warnings }
}

function normalizeHeader(h) {
    return h.trim().toLowerCase()
}

/**
 * Parses CSV text into an array of row arrays, handling quoted fields.
 */
function parseCsvRows(text) {
    const rows = []
    let current = []
    let field = ""
    let inQuotes = false

    for (let i = 0; i < text.length; i++) {
        const ch = text[i]

        if (inQuotes) {
            if (ch === '"') {
                if (i + 1 < text.length && text[i + 1] === '"') {
                    field += '"'
                    i++
                } else {
                    inQuotes = false
                }
            } else {
                field += ch
            }
        } else if (ch === '"') {
            inQuotes = true
        } else if (ch === ",") {
            current.push(field)
            field = ""
        } else if (ch === "\n" || ch === "\r") {
            if (ch === "\r" && i + 1 < text.length && text[i + 1] === "\n") {
                i++
            }
            current.push(field)
            field = ""
            if (current.some((c) => c.trim())) {
                rows.push(current)
            }
            current = []
        } else {
            field += ch
        }
    }

    current.push(field)
    if (current.some((c) => c.trim())) {
        rows.push(current)
    }

    return rows
}

function findColumn(headers, aliases) {
    for (const alias of aliases) {
        const idx = findHeaderIndex(headers, alias)
        if (idx !== -1) return idx
    }
    return -1
}

function findHeaderIndex(headers, alias) {
    return headers.findIndex(headerIncludesAlias)

    function headerIncludesAlias(h) {
        return h.includes(alias)
    }
}

function buildTradeNameLookup(lines) {
    const lookup = {}
    for (const line of lines) {
        const name = (line.tradeName || line.tradeId || "").toLowerCase()
        if (name) {
            lookup[name] = line
        }
    }
    return lookup
}

function parseHourlyRate(raw, rowNum, tradeName, errors, warnings) {
    if (raw === "") {
        warnings.push(`Row ${rowNum}: "${tradeName}" has blank hourly rate, defaulting to 0`)
        return 0
    }
    const num = Number(raw)
    if (!Number.isFinite(num) || num < 0) {
        errors.push(`Row ${rowNum}: "${tradeName}" has invalid hourly rate "${raw}"`)
        return null
    }
    return num
}

// Hourly rate on a fixed-annual line is dormant. A blank export cell must preserve the
// stored value so switching back to hourly after an export/import cycle restores it.
function parseOptionalHourlyRate(raw, existingRate, rowNum, tradeName, errors) {
    if (raw === "") return existingRate ?? 0
    const num = Number(raw)
    if (!Number.isFinite(num) || num < 0) {
        errors.push(`Row ${rowNum}: "${tradeName}" has invalid hourly rate "${raw}"`)
        return null
    }
    return num
}

function parseFixedAnnual(raw, rowNum, tradeName, errors) {
    if (raw === "") return 0
    const num = Number(raw)
    if (!Number.isFinite(num) || num < 0) {
        errors.push(`Row ${rowNum}: "${tradeName}" has invalid fixed annual amount "${raw}"`)
        return null
    }
    return num
}

// Contracted days are optional capacity — blank means unset (null). Returns false on invalid
// input so the caller can skip the row after the pushed error.
function parseContractedDays(raw, rowNum, tradeName, errors) {
    if (raw === "") return null
    const num = Number(raw)
    // Contracted days are consistently 2dp everywhere so display formatting never changes the
    // value; values that round to zero (e.g. 0.001) are rejected rather than stored as 0.
    const rounded = Math.round(num * 100) / 100
    if (!Number.isFinite(num) || !Number.isFinite(rounded) || num <= 0 || rounded <= 0) {
        errors.push(`Row ${rowNum}: "${tradeName}" has invalid contracted days per year "${raw}"`)
        return false
    }
    return rounded
}

function parseMinBillingPeriod(raw, hasColumn, rowNum, tradeName, errors, warnings) {
    if (!hasColumn || raw === "") {
        return undefined
    }
    const num = Number(raw)
    if (!Number.isFinite(num) || !Number.isInteger(num) || num < 0) {
        errors.push(`Row ${rowNum}: "${tradeName}" has invalid minimum billing period "${raw}"`)
        return false
    }
    if (num === 0) {
        warnings.push(`Row ${rowNum}: "${tradeName}" has minimum billing period of 0, treating as empty`)
        return undefined
    }
    return num
}
